import { Moon, Sun } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { flushSync } from "react-dom"

import { cn } from "@/shared/utils"

type ThemeAnimationType =
  | "none"
  | "circle-spread"
  | "round-morph"
  | "swipe-left"
  | "swipe-up"
  | "diag-down-right"
  | "fade-in-out"
  | "shrink-grow"
  | "flip-x-in"
  | "split-vertical"
  | "swipe-right"
  | "swipe-down"
  | "wave-ripple"

type ToggleThemeProps = React.ComponentPropsWithoutRef<"button"> & {
  duration?: number
  animationType?: ThemeAnimationType
}

type DocumentWithViewTransition = Document & {
  startViewTransition?: (callback: () => void) => {
    ready: Promise<void>
    finished: Promise<void>
  }
}

const FLIP_STYLE_ID = "mesa-flip-x-in-vt"

function runShrinkGrow(duration: number) {
  document.documentElement.animate(
    [
      { transform: "scale(0.9)", opacity: 0 },
      { transform: "scale(1)", opacity: 1 },
    ],
    {
      duration: duration * 1.2,
      easing: "cubic-bezier(0.19, 1, 0.22, 1)",
      pseudoElement: "::view-transition-new(root)",
    },
  )
  document.documentElement.animate(
    [
      { transform: "scale(1)", opacity: 1 },
      { transform: "scale(1.05)", opacity: 0 },
    ],
    {
      duration: duration * 1.2,
      easing: "cubic-bezier(0.19, 1, 0.22, 1)",
      pseudoElement: "::view-transition-old(root)",
    },
  )
}

function runCircleSpread(duration: number, x: number, y: number, maxRadius: number) {
  document.documentElement.animate(
    {
      clipPath: [
        `circle(0px at ${x}px ${y}px)`,
        `circle(${maxRadius}px at ${x}px ${y}px)`,
      ],
    },
    {
      duration,
      easing: "ease-in-out",
      pseudoElement: "::view-transition-new(root)",
    },
  )
}

function runAnimation(
  type: ThemeAnimationType,
  duration: number,
  rect: DOMRect,
) {
  const x = rect.left + rect.width / 2
  const y = rect.top + rect.height / 2
  const maxRadius = Math.hypot(
    Math.max(rect.left, window.innerWidth - rect.left),
    Math.max(rect.top, window.innerHeight - rect.top),
  )
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  switch (type) {
    case "shrink-grow":
      runShrinkGrow(duration)
      return
    case "circle-spread":
      runCircleSpread(duration, x, y, maxRadius)
      return
    case "round-morph":
      document.documentElement.animate(
        [
          { opacity: 0, transform: "scale(0.8) rotate(5deg)" },
          { opacity: 1, transform: "scale(1) rotate(0deg)" },
        ],
        {
          duration: duration * 1.2,
          easing: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
          pseudoElement: "::view-transition-new(root)",
        },
      )
      return
    case "swipe-left":
      document.documentElement.animate(
        {
          clipPath: [`inset(0 0 0 ${viewportWidth}px)`, `inset(0 0 0 0)`],
        },
        {
          duration,
          easing: "cubic-bezier(0.2, 0, 0, 1)",
          pseudoElement: "::view-transition-new(root)",
        },
      )
      return
    case "swipe-right":
      document.documentElement.animate(
        {
          clipPath: [`inset(0 ${viewportWidth}px 0 0)`, `inset(0 0 0 0)`],
        },
        {
          duration,
          easing: "cubic-bezier(0.2, 0, 0, 1)",
          pseudoElement: "::view-transition-new(root)",
        },
      )
      return
    case "swipe-up":
      document.documentElement.animate(
        {
          clipPath: [`inset(${viewportHeight}px 0 0 0)`, `inset(0 0 0 0)`],
        },
        {
          duration,
          easing: "cubic-bezier(0.2, 0, 0, 1)",
          pseudoElement: "::view-transition-new(root)",
        },
      )
      return
    case "swipe-down":
      document.documentElement.animate(
        {
          clipPath: [`inset(0 0 ${viewportHeight}px 0)`, `inset(0 0 0 0)`],
        },
        {
          duration,
          easing: "cubic-bezier(0.2, 0, 0, 1)",
          pseudoElement: "::view-transition-new(root)",
        },
      )
      return
    case "diag-down-right":
      document.documentElement.animate(
        {
          clipPath: [
            `polygon(0 0, 0 0, 0 0, 0 0)`,
            `polygon(0 0, 100% 0, 100% 100%, 0 100%)`,
          ],
        },
        {
          duration: duration * 1.5,
          easing: "cubic-bezier(0.4, 0, 0.2, 1)",
          pseudoElement: "::view-transition-new(root)",
        },
      )
      return
    case "fade-in-out":
      document.documentElement.animate(
        { opacity: [0, 1] },
        {
          duration: duration * 0.5,
          easing: "ease-in-out",
          pseudoElement: "::view-transition-new(root)",
        },
      )
      return
    case "wave-ripple":
      document.documentElement.animate(
        {
          clipPath: [
            `circle(0% at 50% 50%)`,
            `circle(${maxRadius}px at 50% 50%)`,
          ],
        },
        {
          duration: duration * 1.5,
          easing: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
          pseudoElement: "::view-transition-new(root)",
        },
      )
      return
    case "split-vertical":
      document.documentElement.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        {
          duration: duration * 0.75,
          easing: "ease-in",
          pseudoElement: "::view-transition-new(root)",
        },
      )
      document.documentElement.animate(
        [
          { clipPath: "inset(0 0 0 0)", transform: "none" },
          { clipPath: "inset(0 40% 0 40%)", transform: "scale(1.2)" },
          { clipPath: "inset(0 50% 0 50%)", transform: "scale(1)" },
        ],
        {
          duration: duration * 1.5,
          easing: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
          pseudoElement: "::view-transition-old(root)",
        },
      )
      return
    case "flip-x-in": {
      if (document.getElementById(FLIP_STYLE_ID)) {return}
      const styleElement = document.createElement("style")
      styleElement.id = FLIP_STYLE_ID
      styleElement.textContent = `
        ::view-transition-group(root) { perspective: 1000px; }
        ::view-transition-old(root) { transform-origin: center; animation: mesa-flip-out 400ms forwards; }
        ::view-transition-new(root) { transform-origin: center; animation: mesa-flip-in 400ms forwards; }
        @keyframes mesa-flip-out { from { transform: rotateY(0deg); opacity: 1; } to { transform: rotateY(-90deg); opacity: 0; } }
        @keyframes mesa-flip-in { from { transform: rotateY(90deg); opacity: 0; } to { transform: rotateY(0deg); opacity: 1; } }
      `
      document.head.appendChild(styleElement)
      return
    }
    case "none":
    default:
      return
  }
}

export function ToggleTheme({
  className,
  duration = 400,
  animationType = "shrink-grow",
  ...props
}: ToggleThemeProps) {
  const [isDark, setIsDark] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const updateTheme = () => {
      setIsDark(document.documentElement.classList.contains("dark"))
    }
    updateTheme()
    const observer = new MutationObserver(updateTheme)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    })
    return () => observer.disconnect()
  }, [])

  const toggleTheme = useCallback(async () => {
    if (!buttonRef.current) {return}

    const applyToggle = () => {
      flushSync(() => {
        const newTheme = !isDark
        setIsDark(newTheme)
        document.documentElement.classList.toggle("dark")
        document.documentElement.style.colorScheme = newTheme ? "dark" : "light"
        try {
          localStorage.setItem("theme", newTheme ? "dark" : "light")
        } catch {
          
        }
      })
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches

    const doc = document as DocumentWithViewTransition
    if (prefersReducedMotion || !doc.startViewTransition) {
      applyToggle()
      return
    }

    const transition = doc.startViewTransition(applyToggle)
    try {
      await transition.ready
    } catch {
      return
    }

    const rect = buttonRef.current.getBoundingClientRect()
    runAnimation(animationType, duration, rect)
  }, [isDark, duration, animationType])

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleTheme}
        aria-label={isDark ? "Ativar tema claro" : "Ativar tema escuro"}
        aria-pressed={isDark}
        className={cn(
          "inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-mesa-border/70 bg-mesa-bg-subtle text-mesa-primary transition-colors duration-300 hover:border-mesa-primary/20 hover:text-mesa-accent-dark",
          className,
        )}
        {...props}
      >
        {isDark ? (
          <Sun className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
        ) : (
          <Moon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
        )}
      </button>

      {animationType !== "flip-x-in" ? (
        <style
          
          dangerouslySetInnerHTML={{
            __html: `
              ::view-transition-old(root),
              ::view-transition-new(root) {
                animation: none;
                mix-blend-mode: normal;
              }
            `,
          }}
        />
      ) : null}
    </>
  )
}
